import {defineStore} from 'pinia'
import api from '@/services/api'
import {useUserRolesStore} from "@/stores/userRoles"
import {useComponentsStore} from "@/stores/components"
import {useNavigationStore} from "@/stores/navigation"

export const useUserStore = defineStore('user', {
    state: () => ({
        exists: false,
        user: false,
        prefs: []
    }),
    
    getters: {
        pref: (state) => (name) => {
            return state.prefs.find(o => o.name === name)?.value
        },
        // todo This is being processed many times, can be optimised?
        access: (state) => (tableName, privilegeName) => {
            const componentsStore = useComponentsStore()
            
            // Product variation hacks. Provides access to all product variation's component
            // if the user has access to the products component.
            // See below for publish specific scripts.
            let variationsTable = tableName.indexOf('product_variations__') === 0
            if (variationsTable) {
                tableName = 'm_products_products'
            }
            
            if (state.user.administrator) {
                // "themes_editor" and "site_tree" do not have components
                let component = componentsStore.items.find(o => o.tableName === tableName)
                if (!component) {
                    //console.log('No component', {tableName})
                    return false
                }
                
                // As administrators have no access privileges their access is governed by the component's
                // settings.
                let access = {
                    add: !!component.addNew,
                    edit: !!component.updateOption,
                    archive: !!component.useArchive,
                    'delete': !!component.deleteOption,
                    publish: !!component.showStatus,
                    'export': true
                }
                
                // These components must not have export/import options
                if (['files', 'product_options', 'product_variables'].indexOf(tableName) > -1) {
                    access.export = false
                }
                
                if (privilegeName) {
                    if (variationsTable && privilegeName === 'publish') {
                        return false
                    }
                    
                    return access[privilegeName]
                }
                
                return access
            }
            
            // No component access
            if (!state.user.access?.[tableName]) {
                return false
            }
            
            if (!privilegeName) {
                return state.user.access[tableName]
            }
            
            // No privilege access
            if (!state.user.access[tableName][privilegeName]) {
                return false
            }
            
            if (
                variationsTable
                && privilegeName === 'publish'
            ) {
                return false
            }
            
            return !!state.user.access[tableName][privilegeName]
        }
    },
    
    actions: {
        reset() {
            this.exists = false
            this.user = false
        },
        
        set(obj) {
            this.user = obj
            
            const navigationStore = useNavigationStore()
            navigationStore.init()
            
            const userRolesStore = useUserRolesStore()
            userRolesStore.init()
            
            return this.getPrefs()
        },
        
        async getPrefs() {
            const response = await api.get('api/component/user_preferences', {
                params: {
                    fields: ['id', 'name', 'value'],
                    userId: this.user.id
                }
            })
            
            this.prefs = response.data.items
            this.exists = true
        },
        
        async setPref({name, value}) {
            let item = this.prefs.find(o => o.name === name)
            
            if (item) {
                const o = await api.patch(`api/component/user_preferences/${item.id}`, {
                    value: value,
                })
                return Object.assign(item, o.data)
            } else {
                const o = await api.post('api/component/user_preferences', {
                    userId: this.user.id,
                    name: name,
                    value: value,
                })
                return this.prefs.push(o.data)
            }
        },
        
        async toggleDarkMode() {
            const isDark = !this.pref('darkMode')
            
            // Allows the login form to use the correct mode.
            //localStorage.setItem('darkMode', isDark)
            
            await this.setPref({
                name: 'darkMode',
                value: isDark
            })
            
            this.applyDarkMode()
        },
        
        applyDarkMode() {
            const isDark = this.pref('darkMode')// || (localStorage.getItem('darkMode') === 'true')
            
            document.querySelectorAll('.btn').forEach((el) => {
                // Prevent BS's button transitions fading between the dark and light styles
                el.classList.add('no-transition')
                
                // Without a timeout no-transition has no affect
                setTimeout(() => {
                    el.classList.remove('no-transition')
                }, 50)
            })
            
            document.documentElement.setAttribute('data-bs-theme', isDark ? 'dark' : 'light')
        }
    }
})